import React, { useEffect } from 'react'
import {
  Input,
  Form,
  Radio,
  Space,
  Row,
  Col,
  Switch,
  Tooltip,
  Popconfirm,
  Button,
  Select,
  FormInstance
} from 'antd'
import { uuid } from 'uuidv4'
import {
  QuestionCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PercentageOutlined,
  PoundOutlined
} from '@ant-design/icons'
import { TextFieldNumber } from './../Widgets'
import {
  requiredRule,
  nonZeroRule,
  rewardValueRule,
  requiredProduct,
  nonEmptySelectRule
} from './Rules'
import ProductPanels from './ProductPanels'
import { Category } from './LoyaltyCardForm'

interface RewardsFormProps {
  loyaltyCardId: string
  categories: Category[] | undefined
  onRemove: (remove: any, rewardId: any, fieldName: any) => void
  handleRewardTypeChange: (value: 'fixed' | 'percentage') => void
  form: FormInstance
  isLoadingCategoriesData: boolean
  getCategoriesData: (shouldQuery: boolean) => void
  shouldQueryOnRewards: boolean
}

const RewardsForm = ({
  loyaltyCardId,
  categories,
  onRemove,
  handleRewardTypeChange,
  form,
  isLoadingCategoriesData,
  getCategoriesData,
  shouldQueryOnRewards
}: RewardsFormProps) => {
  useEffect(() => {
    getCategoriesData(shouldQueryOnRewards)
  }, [])

  return (
    <Form.List name='rewards'>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field, index) => (
            <React.Fragment key={field.key}>
              {fields.length > 1 ? (
                <Form.Item shouldUpdate={true} noStyle>
                  {({ getFieldValue }) => (
                    <Form.Item className='_mb-0'>
                      <Row justify='end' className='_mb-0'>
                        <Popconfirm
                          title='Delete this reward? This action is irreversible!'
                          onConfirm={() =>
                            onRemove(
                              remove,
                              getFieldValue('rewards')[field.key]?.id,
                              field.name
                            )
                          }
                          okText='Delete'
                          cancelText='Cancel'
                        >
                          <Button
                            title='Remove reward'
                            danger
                            icon={<MinusCircleOutlined />}
                            className='-borderless _p-0'
                          >
                            Delete Reward
                          </Button>
                        </Popconfirm>
                      </Row>
                    </Form.Item>
                  )}
                </Form.Item>
              ) : null}
              <Form.Item
                name={[field.name, 'id']}
                fieldKey={[field.key, 'id']}
                initialValue={uuid()}
                style={{ display: 'none' }}
                data-testid={`rewards-form-${field.key}`}
              >
                <Input type='hidden' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'loyalty_card_id']}
                fieldKey={[field.key, 'loyalty_card_id']}
                initialValue={loyaltyCardId}
                style={{ display: 'none' }}
                data-testid={`rewards-loyalty-${field.key}`}
              >
                <Input type='hidden' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'inserted_at']}
                fieldKey={[field.key, 'inserted_at']}
                initialValue={'now()'}
                style={{ display: 'none' }}
                data-testid={`rewards-inserted-${field.key}`}
              >
                <Input type='hidden' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'updated_at']}
                fieldKey={[field.key, 'updated_at']}
                initialValue={'now()'}
                normalize={(value, pre) => value}
                style={{ display: 'none' }}
                data-testid={`rewards-updated-${field.key}`}
              >
                <Input type='hidden' value='now()' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'name']}
                fieldKey={[field.key, 'name']}
                label={
                  <>
                    <span>Reward Title</span>
                    <Tooltip
                      title={`This is the title of the reward you wish to give, it's best to be descriptive eg: 20% off your next order.`}
                    >
                      <QuestionCircleOutlined className='_ml-8 _mr-8' />
                    </Tooltip>
                  </>
                }
                rules={requiredRule}
              >
                <Input data-testid='rewardTitle' type='text' required />
              </Form.Item>
              <Form.Item
                name={[field.name, 'description']}
                fieldKey={[field.key, 'description']}
                label={
                  <>
                    <span>Reward Description (optional)</span>
                    <Tooltip
                      title={
                        'This gives you space to provide some more detail or terms and conditions.'
                      }
                    >
                      <QuestionCircleOutlined className='_ml-8 _mr-8' />
                    </Tooltip>
                  </>
                }
              >
                <Input.TextArea data-testid='rewardDescription' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'earned_reward_message']}
                fieldKey={[field.key, 'earned_reward_message']}
                label={
                  <>
                    <span>Earned reward message (optional)</span>
                    <Tooltip
                      title={
                        'This message displays when a customer earns a reward via the app. It can be left blank and a default message will display.'
                      }
                    >
                      <QuestionCircleOutlined className='_ml-8 _mr-8' />
                    </Tooltip>
                  </>
                }
              >
                <Input.TextArea data-testid='earnedRewardMessage' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'redeem_reward_message']}
                fieldKey={[field.key, 'redeem_reward_message']}
                label={
                  <>
                    <span>Redeem reward message (optional)</span>
                    <Tooltip
                      title={
                        'This message displays when a customer redeems a reward via the app. It can be left blank and a default message will display.'
                      }
                    >
                      <QuestionCircleOutlined className='_ml-8 _mr-8' />
                    </Tooltip>
                  </>
                }
              >
                <Input.TextArea data-testid='redeemRewardMessage' />
              </Form.Item>
              <Form.Item
                name={[field.name, 'required_quantity']}
                fieldKey={[field.key, 'required_quantity']}
                label={
                  <>
                    <span>Stamps needed to claim reward</span>
                    <Tooltip
                      title={`This is the number of stamps needed to claim this reward. EG: if it's 50 the user will get access to this reward after they earn 50 stamps.`}
                    >
                      <QuestionCircleOutlined className='_ml-8 _mr-8' />
                    </Tooltip>
                  </>
                }
                rules={nonZeroRule}
              >
                <TextFieldNumber
                  data-testid='requiredQuantity'
                  inputType='number'
                  min={1}
                  max={999}
                  step={1}
                  style={{ maxWidth: '160px' }}
                />
              </Form.Item>
              <Row className='_mb-0'>
                <Col span={8}>
                  <Form.Item
                    name={[field.name, 'reward_value_type']}
                    fieldKey={[field.key, 'reward_value_type']}
                    initialValue={'fixed'}
                    rules={requiredRule}
                    label={
                      <>
                        <span>Reward type</span>
                        <Tooltip
                          title={
                            'This allows you to choose what type of reward to give.'
                          }
                        >
                          <QuestionCircleOutlined className='_ml-8 _mr-8' />
                        </Tooltip>
                      </>
                    }
                  >
                    <Radio.Group
                      data-testid='rewardValueType'
                      onChange={(e) => handleRewardTypeChange(e.target.value)}
                    >
                      <Space direction='vertical' align='start'>
                        <Radio data-testId='rewardValueFixed' value='fixed'>
                          Fixed
                        </Radio>
                        <Radio
                          data-testId='rewardValuePercentage'
                          value='percentage'
                        >
                          Percentage
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues?.rewards[index]?.reward_value_type !==
                      currentValues?.rewards[index]?.reward_value_type
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('rewards')[index]?.reward_value_type ===
                      'percentage' ? (
                        <Form.Item
                          name={[field.name, 'reward_value']}
                          fieldKey={[field.key, 'reward_value']}
                          label='Reward value'
                          dependencies={['reward_value_type']}
                          rules={rewardValueRule}
                        >
                          <Input
                            data-testid='rewardValue'
                            type='number'
                            min={1}
                            step={0.01}
                            addonAfter={<PercentageOutlined />}
                            style={{
                              maxWidth: '6rem'
                            }}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name={[field.name, 'reward_value']}
                          fieldKey={[field.key, 'reward_value']}
                          label='Reward value'
                          dependencies={['reward_value_type']}
                          rules={rewardValueRule}
                        >
                          <Input
                            data-testid='rewardValue'
                            type='number'
                            min={1}
                            step={0.01}
                            addonAfter={<PoundOutlined />}
                            style={{
                              maxWidth: '6rem'
                            }}
                          />
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name={[field.name, 'reward_type']}
                fieldKey={[field.key, 'reward_type']}
                label={
                  <>
                    <span>Specific product/s</span>
                    <Tooltip
                      title={
                        'Apply the reward discount only to specific products or categories of product eg: 50% off Pizzas or 100% off one coffee.'
                      }
                    >
                      <QuestionCircleOutlined className='_ml-8 _mr-8' />
                    </Tooltip>
                  </>
                }
                valuePropName='checked'
                getValueProps={(value) => {
                  return { checked: value === 'product' }
                }}
                getValueFromEvent={(checked) => {
                  return checked ? 'product' : 'value'
                }}
                initialValue={'value'}
                className='_pt-4 _pb-4'
              >
                <Switch
                  className='_ml-12'
                  onChange={(checked) => getCategoriesData(checked)}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues?.rewards[index]?.reward_type !==
                  currentValues?.rewards[index]?.reward_type
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('rewards')[index]?.reward_type ===
                    'product' && (
                    <>
                      <Form.Item
                        name={[field.name, 'product_variant_ids']}
                        fieldKey={[field.key, 'product_variant_ids']}
                        label={
                          <Row
                            justify='space-between'
                            style={{ width: '100%' }}
                          >
                            <Col>Choose product/s</Col>
                            <Col>
                              {getFieldValue('rewards')[index]
                                ?.product_variant_ids?.length || 0}{' '}
                              selected
                            </Col>
                          </Row>
                        }
                        rules={
                          getFieldValue('rewards')[index]?.product_variant_ids
                            ?.length > 0
                            ? []
                            : requiredProduct
                        }
                        className='loyalty-product-label'
                      >
                        <ProductPanels
                          categories={categories}
                          selectedProductVariantIds={
                            getFieldValue('rewards')[index]
                              ?.product_variant_ids ?? []
                          }
                          form={form}
                          formType='rewards'
                          index={index}
                          isLoadingCategoriesData={isLoadingCategoriesData}
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'variants_only']}
                        fieldKey={[field.key, 'variants_only']}
                        valuePropName='checked'
                        initialValue={false}
                        label={
                          <>
                            <span>Apply only to variant price?</span>
                            <Tooltip
                              title={
                                'Decide if the discount includes modifiers and extras.'
                              }
                            >
                              <QuestionCircleOutlined className='_ml-8 _mr-8' />
                            </Tooltip>
                          </>
                        }
                        className='_pt-4 _pb-4'
                      >
                        <Switch className='_ml-12' />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'product_limit_enabled']}
                        fieldKey={[field.key, 'product_limit_enabled']}
                        valuePropName='checked'
                        initialValue={
                          null ||
                          getFieldValue('rewards')[field.key]?.product_limit
                        }
                        label={
                          <>
                            <span>Limit product reward</span>
                            <Tooltip
                              title={
                                'Choose how many products the discount applies for: eg: if 1 then only 1 qualifying product will be discounted, if 10 then up to 10 will be.'
                              }
                            >
                              <QuestionCircleOutlined className='_ml-8 _mr-8' />
                            </Tooltip>
                          </>
                        }
                        className='_pt-4 _pb-4'
                      >
                        <Switch className='_ml-12' />
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues?.rewards[index]?.product_limit_enabled !==
                          currentValues?.rewards[index]?.product_limit_enabled
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue('rewards')[index]
                            ?.product_limit_enabled && (
                            <Form.Item
                              name={[field.name, 'product_limit']}
                              fieldKey={[field.key, 'product_limit']}
                              label='Product quantity'
                              initialValue={1}
                              rules={nonZeroRule}
                            >
                              <TextFieldNumber
                                inputType='number'
                                data-testid='rewardProductLimit'
                                min={1}
                                step={1}
                                style={{ maxWidth: '160px' }}
                              />
                            </Form.Item>
                          )
                        }
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>
              <Form.Item
                name={[field.name, 'minimum_value_enabled']}
                fieldKey={[field.key, 'minimum_value_enabled']}
                valuePropName='checked'
                label={
                  <>
                    <span>Minimum spend limit enabled</span>
                  </>
                }
                className='_pt-4 _pb-4'
              >
                <Switch className='_ml-12' />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues?.rewards[index]?.minimum_value_enabled !==
                  currentValues?.rewards[index]?.minimum_value_enabled
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('rewards')[index]?.minimum_value_enabled && (
                    <>
                      <Form.Item
                        name={[field.name, 'minimum_value']}
                        fieldKey={[field.key, 'minimum_value']}
                        label='Minimum spend limit'
                        initialValue={1}
                        rules={nonZeroRule}
                      >
                        <TextFieldNumber
                          inputType='number'
                          data-testid='minimumSpendLimit'
                          min={0}
                          step={0.01}
                          style={{ maxWidth: '160px' }}
                        />
                      </Form.Item>
                      <Form.Item
                        label='Fulfilment types'
                        name={[field.name, 'minimum_value_fulfillments']}
                        fieldKey={[field.key, 'minimum_value_fulfillments']}
                        rules={nonEmptySelectRule}
                      >
                        <Select
                          mode='multiple'
                          placeholder='Apply to fulfilment types'
                          showSearch
                          filterOption={(input, option) =>
                            option?.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {['delivery', 'pickup', 'table'].map((value) => (
                            <Select.Option value={value}>{value}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>
              <Form.Item shouldUpdate={true} noStyle>
                {({ getFieldValue }) => (
                  <>
                    <Form.Item
                      name={[field.name, 'expiration_enabled']}
                      fieldKey={[field.key, 'expiration_enabed']}
                      valuePropName='checked'
                      initialValue={
                        null ||
                        getFieldValue('rewards')[field.key]?.months_expiration
                      }
                      label='Reward expiration enabled'
                      className='_pt-4 _pb-4'
                    >
                      <Switch className='_ml-12' />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues?.rewards[index]?.expiration_enabled !==
                        currentValues?.rewards[index]?.expiration_enabled
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue('rewards')[index]?.expiration_enabled && (
                          <Form.Item
                            name={[field.name, 'months_expiration']}
                            fieldKey={[field.key, 'months_expiration']}
                            initialValue={2}
                            rules={nonZeroRule}
                            label={
                              <>
                                <span>Rewards expiration (Months)</span>
                                <Tooltip
                                  title={`This is selecting the number of months an earned reward will expire if unused. If 6 months is selected, and a customer earns a reward on the January 1st, they have until June 1st to use the reward.`}
                                >
                                  <QuestionCircleOutlined className='_ml-8 _mr-8' />
                                </Tooltip>
                              </>
                            }
                          >
                            <TextFieldNumber
                              inputType='number'
                              data-testid='rewardMonthsExpiration'
                              min={1}
                              step={1}
                            />
                          </Form.Item>
                        )
                      }
                    </Form.Item>
                  </>
                )}
              </Form.Item>
            </React.Fragment>
          ))}
          <Form.Item>
            <Button
              title='Add reward'
              ghost
              onClick={() => {
                add()
              }}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Add Reward
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  )
}

export default RewardsForm
